import { httpsCallable } from "firebase/functions";
import { functions } from "../lib/firebase";
import type {
  UpdateUserOnBehalfInput,
  UpdateUserOnBehalfResponse,
  CreateContactInput,
  CreateContactResponse,
  UpdateContactInput,
  UpdateContactResponse,
  InviteToEventInput,
  InviteToEventResponse,
  BulkInviteToEventInput,
  BulkInviteToEventResponse,
  BulkResolveAttendeesInput,
  BulkResolveAttendeesResponse,
  BulkResolveUsersInput,
  BulkResolveUsersResponse,
  DeleteUserInput,
  DeleteUserResponse,
  DeleteContactInput,
  DeleteContactResponse,
  RemoveFromEventInput,
  RemoveFromEventResponse,
  Role,
} from "@event-planner/shared";

const updateUserOnBehalfCallable = httpsCallable<
  UpdateUserOnBehalfInput,
  UpdateUserOnBehalfResponse
>(functions, "updateUserOnBehalf");

const createContactCallable = httpsCallable<
  CreateContactInput,
  CreateContactResponse
>(functions, "createContact");

const updateContactCallable = httpsCallable<
  UpdateContactInput,
  UpdateContactResponse
>(functions, "updateContact");

const inviteToEventCallable = httpsCallable<
  InviteToEventInput,
  InviteToEventResponse
>(functions, "inviteToEvent");

const bulkInviteToEventCallable = httpsCallable<
  BulkInviteToEventInput,
  BulkInviteToEventResponse
>(functions, "bulkInviteToEvent");

const bulkResolveAttendeesCallable = httpsCallable<
  BulkResolveAttendeesInput,
  BulkResolveAttendeesResponse
>(functions, "bulkResolveAttendees");

export async function bulkResolveAttendees(
  input: BulkResolveAttendeesInput,
): Promise<BulkResolveAttendeesResponse> {
  const result = await bulkResolveAttendeesCallable(input);
  return result.data;
}

const bulkResolveUsersCallable = httpsCallable<
  BulkResolveUsersInput,
  BulkResolveUsersResponse
>(functions, "bulkResolveUsers");

export async function bulkResolveUsers(
  input: BulkResolveUsersInput,
): Promise<BulkResolveUsersResponse> {
  const result = await bulkResolveUsersCallable(input);
  return result.data;
}

export async function updateUserOnBehalf(
  input: UpdateUserOnBehalfInput,
): Promise<UpdateUserOnBehalfResponse> {
  const result = await updateUserOnBehalfCallable(input);
  return result.data;
}

export async function createContact(
  input: CreateContactInput,
): Promise<CreateContactResponse> {
  const result = await createContactCallable(input);
  return result.data;
}

export async function updateContact(
  input: UpdateContactInput,
): Promise<UpdateContactResponse> {
  const result = await updateContactCallable(input);
  return result.data;
}

export async function inviteToEvent(
  input: InviteToEventInput,
): Promise<InviteToEventResponse> {
  const result = await inviteToEventCallable(input);
  return result.data;
}

/**
 * Bulk variant. Single round-trip for an array of emails. Backend uses
 * BulkWriter for parallel writes; the client just hands off the email
 * list and renders the per-row results returned in the response.
 *
 * Used by ExcelAttendeeImportDialog for demo-scale Excel uploads.
 */
export async function bulkInviteToEvent(
  input: BulkInviteToEventInput,
): Promise<BulkInviteToEventResponse> {
  const result = await bulkInviteToEventCallable(input);
  return result.data;
}

// ── Account activation ──

interface ActivateAccountResponse {
  matched: boolean;
  alreadySetUp?: boolean;
  role?: string;
  displayName?: string;
  claimedCount?: number;
}

const activateAccountCallable = httpsCallable<
  Record<string, never>,
  ActivateAccountResponse
>(functions, "activateAccount");

export async function activateAccount(): Promise<ActivateAccountResponse> {
  const result = await activateAccountCallable({});
  return result.data;
}

// ── Role management ──

interface SetUserRoleInput {
  targetUserId: string;
  role: Role;
}

interface SetUserRoleResponse {
  success: true;
  targetUserId: string;
  role: Role;
}

const setUserRoleCallable = httpsCallable<
  SetUserRoleInput,
  SetUserRoleResponse
>(functions, "setUserRole");

export async function setUserRole(
  input: SetUserRoleInput,
): Promise<SetUserRoleResponse> {
  const result = await setUserRoleCallable(input);
  return result.data;
}

// ── Account deletion ──

const deleteUserCallable = httpsCallable<DeleteUserInput, DeleteUserResponse>(
  functions,
  "deleteUser",
);

export async function deleteUser(
  input: DeleteUserInput,
): Promise<DeleteUserResponse> {
  const result = await deleteUserCallable(input);
  return result.data;
}

const deleteContactCallable = httpsCallable<
  DeleteContactInput,
  DeleteContactResponse
>(functions, "deleteContact");

export async function deleteContact(
  input: DeleteContactInput,
): Promise<DeleteContactResponse> {
  const result = await deleteContactCallable(input);
  return result.data;
}

// ── Remove from event (ADR-089) ──
// Unifies the former revokePendingInvitation + removeUserFromEvent. The
// caller passes whichever identifier is on hand; the function handles
// both pending invitations and active members idempotently.

const removeFromEventCallable = httpsCallable<
  RemoveFromEventInput,
  RemoveFromEventResponse
>(functions, "removeFromEvent");

export async function removeFromEvent(
  input: RemoveFromEventInput,
): Promise<RemoveFromEventResponse> {
  const result = await removeFromEventCallable(input);
  return result.data;
}
